/**
 * dateLocale — Helpers de formatação de datas dependentes do locale ativo.
 *
 * Regra do produto (Phase 8.38):
 *   • pt-BR  →  DD/MM/AA
 *   • en-US  →  MM/DD/AA
 *
 * O locale ativo vem do `i18next` (mesmo mecanismo usado para textos da UI).
 * A empresa/workspace define o idioma padrão em `TenantContext` chamando
 * `i18n.changeLanguage(...)`. Portanto basta ler `i18n.language`.
 *
 * Todas as funções são "no-op safe" para valores ausentes e sempre retornam
 * strings — jamais lançam.
 */
import i18n from "@/i18n";

export function getActiveLocale() {
  return i18n?.language || "pt-BR";
}

/** Converte ISO "YYYY-MM-DD" para `Date` em MEIA-NOITE LOCAL (evita drift
 *  de fuso, ex.: pt-BR UTC-3 fazia "2026-09-03" virar 02/09 na exibição). */
function isoToLocalDate(iso) {
  if (iso instanceof Date) return iso;
  if (typeof iso !== "string") return new Date(iso);
  // Aceita "YYYY-MM-DD" e "YYYY-MM-DDTHH:mm:ss..." — se não tiver "T",
  // ancoramos como meia-noite local.
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const [, y, mo, d] = m;
    return new Date(Number(y), Number(mo) - 1, Number(d));
  }
  return new Date(iso);
}

/** Data ISO → padrão curto numérico do locale ativo (DD/MM/AA ou MM/DD/AA). */
export function fmtDataBR(iso, locale) {
  if (!iso) return "—";
  try {
    const lng = locale || getActiveLocale();
    return isoToLocalDate(iso).toLocaleDateString(lng, {
      day: "2-digit", month: "2-digit", year: "2-digit",
    });
  } catch { return String(iso); }
}

/** Rótulo curto de dia da semana (ex: "seg", "mon") no locale ativo. */
export function fmtWeekdayShort(date, locale) {
  const lng = locale || getActiveLocale();
  return date.toLocaleDateString(lng, { weekday: "short" }).slice(0, 3);
}

/** Rótulo curto de mês (ex: "ago", "aug") no locale ativo, sem ponto final. */
export function fmtMonthShort(date, locale) {
  const lng = locale || getActiveLocale();
  return date.toLocaleDateString(lng, { month: "short" }).replace(".", "");
}
